/**
 * ConversationData.js
 * Talkroute
 *
 * Created by Vel Genov on 3/13/19
 * Copyright (c) 2019 Talkroute. All right reserved.
 */
import moment, { Moment } from "moment";
import {
  textMessageJSONParser,
  rehydrateTextMessage,
  TextMessageData,
  MessageDirection,
} from "./TextMessageData";

import uiString from "../../constants/uiString";
import { getFormattedDate, getSimpleFormattedDateAndTime, getSimpleFormattedTime } from "@helpers/functions/dateFormatter";
import validateArray from "@helpers/functions/arrayValidator";
import { IConversationV1 } from "@models/Conversation.models";
import { IContact } from "@models/Contacts.models";
import { formatPhoneNumber } from "@helpers/functions/phoneNumberFormatter";

export const NEW_CONVERSATION_ID = "NEW_CONVERSATION";
export const WELCOME_CONVERSATION = uiString.welcomeTextMessage.FROM;
export const MESSAGE_FETCH_LIMIT = 25; // Used by the mock data service

/**
 * A data class for conversation data
 *
 * @param conversationId the ID of the conversation. In the format of talkrouteNumber-contactNumber
 * @param contactNumber the of the other participat in the conversation
 * @param talkrouteNumber the Talkroute number used for this conversation
 * @param lastMessageAt a moment object representing the date of the last message
 * @param lastMessage a TextMessageData object representing the last message
 */
export class ConversationData {
  public conversationId: string;
  public contactNumber: string;
  public talkrouteNumber: string;
  public lastMessageAt: Moment;
  public lastMessage: TextMessageData;
  public messages: any;
  public messagesOnServer: any;
  public lastMessageFetch: number | null;
  public contact?: IContact;
  public messageCount: any;

  constructor(opts) {
    this.conversationId = opts.conversationId;
    this.contactNumber = opts.contactNumber;
    this.talkrouteNumber = opts.talkrouteNumber;
    this.lastMessageAt = moment(opts.lastMessageAt);
    this.lastMessage = opts.lastMessage;
    this.messages = opts.messages ? opts.messages : {};
    this.contact = opts.contact;
    this.messagesOnServer = null;
    this.lastMessageFetch = null;
    this.messageCount = opts.messageCount;
  }

  /**
   * Getter for the formatted date and time
   *
   * @returns the date and time of the last message formatted
   */
  getSimpleFormattedDateAndTime = () =>
    getSimpleFormattedDateAndTime(this.lastMessageAt);

  /**
   * Getter for the formatted time
   *
   * @returns the time of the last message formatted
   */
  getSimpleFormattedTime = () =>
  getSimpleFormattedTime(this.lastMessageAt);

  /**
   * Getter for the formatted date
   *
   * @returns the date of the last message formatted
   */
  getFormattedDate = () => getFormattedDate(this.lastMessageAt);

  /**
   * Getter fot the contactNumber
   *
   * @returns the contact phone number
   */
  getCallerPhoneNumber = () => this.contactNumber;

  /**
   * Return the caller contact name if available.
   * 
   * @returns contact name or phone number
   */
  getContactInfo = () => {
   if (this.contact) return this.contact.name;
   const number = this.getCallerPhoneNumber();
   if (number[0] === '1') return  formatPhoneNumber(number);
   return number;
  }

  setContactInfo = (contact: IContact) => { 
    return new ConversationData({
      conversationId: this.conversationId,
      contactNumber: this.contactNumber,
      talkrouteNumber: this.talkrouteNumber,
      lastMessageAt: this.lastMessageAt,
      lastMessage: this.lastMessage,
      messages: this.messages,
      messagesOnServer: this.messagesOnServer,
      lastMessageFetch: this.lastMessageFetch,
      contact: contact,
    })
  }

  setContactInfoForTextMessageHistory = (contact: IContact) => {
    this.contact = contact;
    return this;
  }
  /**
   * Checks if this is a new conversation
   *
   * @returns a boolean indicating wheather this is a new conversation
   */
  isNewConversation = () => this.conversationId === NEW_CONVERSATION_ID;

  /**
   * Generate an ID for new conversations usign the following format
   *
   * talkrouteNumber-contactNumber
   *
   * @returns the new ID
   */
  generateConversationId = () => {
    if (this.contactNumber !== null && this.talkrouteNumber !== null) {
      const conversationId = `${this.talkrouteNumber.replace("+", "")}-${this.contactNumber.replace("+", "")}`;

      return conversationId;
    }
  };

  /**
   * Sets the last message fetch timestamp
   *
   * @param lastFetch the timestamp to use
   */
  setLastMessageFetch = (lastFetch) => {
    this.lastMessageFetch = lastFetch;
  };

  /**
   * Sets the total number of messages available on the server
   *
   * @param totalMessages the total number of messages on the server
   */
  setTotalMessages = (totalMessages) => {
    this.messagesOnServer = totalMessages;
  };

  /**
   * Sort messages by date in an descending order
   */
  sortMessages = () => {
    if (this.messages) {
      // Create items array
      const dataArray = Object.keys(this.messages).reduce(
        (result: any[], key) => {
          result.push(this.messages[key]);
          return result;
        },
        []
      );

      // Sort by data before returning
      dataArray.sort(
        (a: any, b: any) =>
          // A simple comparator function that uses the numeric createdAtMicroseconds values for comparison
          a.createdAtMicroseconds - b.createdAtMicroseconds
      );

      this.messages = dataArray.reduce((map, message) => {
        const result = map;
        result[message.id] = message;
        return result;
      }, {});
    }
  };

  /**
   * Merge the current conversation with an updated one
   *
   * @param updateConversation the updated conversation to merge with
   */
  mergeWithUpdatedConversation = (updatedConversation) => {
    const { conversationId: newConversationId } = updatedConversation;

    if (this.conversationId === newConversationId) {
      // Make sure we are merging the same conversations
      const {
        lastMessageAt: newLastMessageAt,
        lastMessage: newLastMessage,
      } = updatedConversation;

      this.addMessages({ messages: [newLastMessage] });

    } else {
    }
  };

  /**
   * Adds new messages and updates all related data
   * New messages are merged with the existing ones, and the list trimmed to only unique message IDs
   * The remaining list of messages is sorted by date
   *
   * @param messages the list of messages to merge
   * @param pagination pagination data containing the number of messages available on the server
   */
  addMessages = (props) => {
    const { messages, pagination } = props;
    if (pagination) {
      const { count, timestamp } = pagination;

      this.setLastMessageFetch(timestamp);
      this.setTotalMessages(count);
    }
    const oldMessages = this.messages;

    this.messages = messages.reduce((map, message) => {
      const result = map;
      result[message.id] = message;
      return result;
    }, {});

    Object.assign(this.messages, oldMessages);

    this.sortMessages();
    this.setLastMessage();
  };

  /**
   * Trim the list of messages leaving only unique ones
   * Keeping the last instance of each duplicate
   */
  trimDuplicateMessages = () => {
    const dataArray = Object.keys(this.messages).reduce(
      (result: any[], key) => {
        result.push(this.messages[key]);
        return result;
      },
      []
    );

    const trimmedMessages = [
      ...new Map(dataArray.map((message) => [message.id, message])).values(),
    ];

    this.messages = trimmedMessages.reduce((map, message) => {
      const result = map;
      result[message.id] = message;
      return result;
    }, {});
  };

  /**
   * Searches through all existing messages for the one with a "isLast" flag.
   * Sets the last message to it
   */
  setLastMessage = () => {
    if (this.messages) {
      const lastMessageKey = Object.keys(this.messages)[
        Object.keys(this.messages).length - 1
      ];
      const lastMessage = this.messages[lastMessageKey];
      this.lastMessage = lastMessage;
      this.lastMessageAt = lastMessage.createdAt;
    }
  };

  /**
   * @returns a count of the locally stored messages. Returns 0 if local messages are empty
   */
  getLocalMessagesCount = () => {
    if (this.messages) {
      return Object.keys(this.messages).length;
    }

    // If not a valid array, we don't have any messages stored
    return 0;
  };
}

/**
 * Create a new conversation draft with a talkroutePhoneNumber
 *
 * @param {*} talkroutePhoneNumber
 */
export const createNewConversation = (talkroutePhoneNumber) => {
  const newConversation = new ConversationData({
    conversationId: NEW_CONVERSATION_ID,
    contactNumber: null,
    talkrouteNumber: talkroutePhoneNumber,
    lastMessageAt: null,
    lastMessage: null,
  });

  return newConversation;
};

/**
 * Create the Welcome Conversation and add 3 welcome messages to it
 *
 * All text for the Welcome Conversation is defined in uiString
 */
export const createWelcomeConversation = (isLastMessageRead: boolean = false) => {
  const welcomeConversation = new ConversationData({
    conversationId: WELCOME_CONVERSATION,
    contactNumber: uiString.welcomeTextMessage.FROM,
    talkrouteNumber: uiString.welcomeTextMessage.FROM,
    lastMessageAt: moment(),
    lastMessage: uiString.welcomeTextMessage.LAST_MESSAGE_TEXT,
  });

  const message1CreatedAt = moment();
  const message1 = new TextMessageData({
    body: uiString.welcomeTextMessage.MESSAGE_1_BODY,
    read: false,
    isLast: false,
    userEmail: uiString.welcomeTextMessage.FROM,
    talkrouteNumber: uiString.welcomeTextMessage.FROM,
    contactNumber: uiString.welcomeTextMessage.FROM,
    direction: MessageDirection.INCOMING,
    id: "1",
    createdAtMicroseconds: message1CreatedAt.unix(),
    createdAt: message1CreatedAt,
    updatedAt: message1CreatedAt,
    attachments: null,
  });

  const message2CreatedAt = moment();
  const message2 = new TextMessageData({
    body: uiString.welcomeTextMessage.MESSAGE_2_BODY,
    read: false,
    isLast: false,
    userEmail: uiString.welcomeTextMessage.FROM,
    talkrouteNumber: uiString.welcomeTextMessage.FROM,
    contactNumber: uiString.welcomeTextMessage.FROM,
    direction: MessageDirection.INCOMING,
    id: "2",
    createdAtMicroseconds: message2CreatedAt.unix(),
    createdAt: message2CreatedAt,
    updatedAt: message2CreatedAt,
    attachments: null,
  });
  const message3CreatedAt = moment();
  const message3 = new TextMessageData({
    body: uiString.welcomeTextMessage.MESSAGE_3_BODY,
    read: isLastMessageRead,
    isLast: true,
    userEmail: uiString.welcomeTextMessage.FROM,
    talkrouteNumber: uiString.welcomeTextMessage.FROM,
    contactNumber: uiString.welcomeTextMessage.FROM,
    direction: MessageDirection.INCOMING,
    id: "3",
    createdAtMicroseconds: message3CreatedAt.unix(),
    createdAt: message3CreatedAt,
    updatedAt: message3CreatedAt,
    attachments: null,
  });

  welcomeConversation.addMessages({ messages: [message1, message2, message3] });
  welcomeConversation.messagesOnServer = 3;

  return welcomeConversation;
};

/**
 * Create a new conversation with a talkroutePhoneNumber and a contactNumber
 *
 * Using a new conversation here, instead of updating the existing one
 * This is done to trigger a Redux update, since redux does only a shallow search when objects are updated
 *
 * @param {*} contactNumber
 * @param {*} talkrouteNumber
 */
export const updateConversation = (props) => {
  const { contactNumber, talkrouteNumber } = props;
  const newConversation = new ConversationData({
    conversationId: NEW_CONVERSATION_ID,
    contactNumber,
    talkrouteNumber,
    lastMessageAt: null,
    lastMessage: null,
  });

  return newConversation;
};

/**
 * Parses the incoming JSON conversations data. Creates a ConversationData for each line item
 *
 * @param {*} conversationData an array conversation data.
 */
export const conversationJSONParser = (conversationData: IConversationV1[]) => {
  // If valid data is passed, proceed. Otherwise don't return anything
  if (validateArray(conversationData)) {
    // Process each entry
    const parsedData = conversationData.map((entry) => {
      const {
        conversation_id: conversationId,
        contact_number: contactNumber,
        talkroute_number: talkrouteNumber,
        last_message_at: lastMessageAt,
        last_message: lastMessageUnparsed,
        messages_count: messageCount,
        
      } = entry;

      const message = textMessageJSONParser([lastMessageUnparsed]);
      let lastMessage = message;
      let messages;
      if (entry.data) {
        messages = textMessageJSONParser(entry.data);
      }
      if (validateArray(message)) {
        lastMessage = message.pop();
      }

      const conversationEntry = new ConversationData({
        conversationId,
        contactNumber,
        talkrouteNumber,
        lastMessageAt,
        lastMessage,
        messages,
        messageCount,
      });

      return conversationEntry;
    });

    return parsedData;
  } else {
    return [];
  }
};

// use conversationJSONParser to parse a single conversation to a conversationData instance
export const parseConversationToData = (conversation: IConversationV1) => {
  return conversationJSONParser([conversation]).pop() as ConversationData;
}
/**
 * Parses the incoming generic objects into ConversationData objects.
 * Checks for empty objects passed in
 *
 * @param {*} conversationData an array containig conversations.
 */
export const conversationsRehydrator = (conversationData) => {
  // If valid data is passed, proceed. Otherwise don't return anything
  if (validateArray(conversationData)) {
    // Process each entry
    const parsedData = conversationData.map((entry) =>
      rehydrateConversation(entry)
    );

    return parsedData;
  }
  return [];
};

/**
 * Parses the incoming single generic object into ConversationData.
 * Checks for an empty object being passed in.
 *
 * @param {*} conversation a single generic object containing a conversation.
 */
export const rehydrateConversation = (conversation) => {
  if (conversation) {
    const {
      conversationId,
      contactNumber,
      talkrouteNumber,
      lastMessageAt,
      lastMessage,
      messages,
    } = conversation;

    const rehydratedMessage = rehydrateTextMessage(lastMessage);

    let rehydratedMessages: any[] = [];
    // Rehydrate the messages only for the welcome conversation
    if (conversationId === WELCOME_CONVERSATION) {
      if (messages) {
        rehydratedMessages = [];
        Object.keys(messages).forEach((key) => {
          const currentMessage = rehydrateTextMessage(messages[key]);
          rehydratedMessages.push(currentMessage);
        });
      }
    }

    const conversationEntry = new ConversationData({
      conversationId,
      contactNumber,
      talkrouteNumber,
      lastMessageAt,
      lastMessage: rehydratedMessage,
      messages: rehydratedMessages,
    });

    return conversationEntry;
  }
  return conversation;
};
